import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import styles from './style.module.scss';
import Image from 'next/image';
import { commercialTestIds } from 'constants/test-constants';
import Video from 'components/video';
import HtmlTrans from 'components/html-trans';

import type { CommercialMetricsConfig, CommercialMetrics as CommercialMetric } from '@tenants';
import { Stack } from '@mui/material';

const MetricItem = ({ value, label }: CommercialMetric) => (
  <div className={styles.metricItem}>
    <div className={styles.metricValue}>{value}</div>
    <div className={styles.metricLabel}>{label}</div>
  </div>
);

const CommercialMetrics = ({ metrics, backgroundVideo, logo, align = 'left', showNasdaq = false, title = '' }: CommercialMetricsConfig) => {
  const scrollContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollContent = scrollContentRef.current;

    if (!scrollContent) return;

    // Get the width of one set of metrics (half the total content width)
    const totalWidth = scrollContent.offsetWidth;
    const singleSetWidth = totalWidth / 2;

    // Set initial position
    gsap.set(scrollContent, { x: 0 });

    // Create the infinite scroll animation
    const tl = gsap.timeline({ repeat: -1 });

    tl.to(scrollContent, {
      x: -singleSetWidth,
      duration: 15,
      ease: 'none',
    })
      .set(scrollContent, { x: 0 }); // Reset position instantly

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <section className={styles.metricsSection}>
      <Video playsInline loop src={backgroundVideo.src} type={backgroundVideo.type} className={styles.backgroundVideo} />
      <div className={styles.videoOverlay}></div>
      <div className={styles.container}>
        <Stack className={styles.header} direction="column" alignItems={align === 'center' ? 'center' : 'flex-start'} justifyContent="center" spacing={'18px'}>
          <div className={styles.logo}><Image src={logo.src} alt={logo.alt} width={logo.width} height={logo.height} />
            {showNasdaq && <span>Nasdaq: AGNT</span>}
          </div>
          {title && <h3 style={{ textAlign: align === 'center' ? 'center' : 'left' }}><HtmlTrans i18nKey={title} /></h3>}
        </Stack>
        <div className={styles.scrollContainer}>
          <div className={styles.scrollContent} ref={scrollContentRef}>
            <div className={styles.metricsGrid}>
              {metrics.map((metric, index) => (
                <MetricItem
                  key={`original-${index}`}
                  value={metric.value}
                  label={metric.label}
                />
              ))}
            </div>
            {/* Duplicate content for seamless loop */}
            <div data-testid={commercialTestIds.duplicateMetrics} aria-hidden="true" className={styles.metricsGrid}>
              {metrics.map((metric, index) => (
                <MetricItem
                  key={`duplicate-${index}`}
                  value={metric.value}
                  label={metric.label}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommercialMetrics;
